import React, { useMemo } from "react";
import { AxiosRequestConfig } from "axios";
import queryString from "query-string";
import { Box } from '@mui/material';
import CustomButton from 'src/core/ui/CustomButton';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

// const mapToInitialValues = (responseData, prefix = "") => {
//   let initalValues = {};
//   if (!responseData) return null;
//   Object.entries(responseData).forEach((entry: [string, any]) => {
//     const [key, value] = entry;
//     if (value?.Key || value?.Id) {
//       const nestedKey = (value?.Key ?? value?.Id) as string | number;
//       initalValues[prefix + key] = nestedKey;
//       initalValues[prefix + key + "_Value"] = ((value?.Value ?? value?.Name) ?? "") as string | number;
//     } else {
//       initalValues[prefix + key] = value ?? "";
//     }
//   });
//   return initalValues;
// }

const mapToInitialValues = (responseData: any, prefix = "") => {
  let initialValues: Record<string, any> = {};
  if (!responseData) return null;
  const processNestedObject = (object: any, objectPrefix: string) => {
    Object.entries(object).forEach(([nestedKey, nestedValue]) => {
      const fullKey = objectPrefix ? objectPrefix + nestedKey : nestedKey;
      const value = nestedValue === null ? "" : nestedValue;
      if (value && typeof value === "object" && !Array.isArray(value)) {
        processNestedObject(value, fullKey + "_");
      } else if (objectPrefix && (nestedKey === "Key" || nestedKey === "Id")) {
        initialValues[objectPrefix.slice(0, -1)] = value;
      } else if (objectPrefix && (nestedKey === "Value" || nestedKey === "Name")) {
        initialValues[objectPrefix + nestedKey] = value;
        initialValues[objectPrefix + "Value"] = value;
      } else {
        initialValues[fullKey] = value;
      }
    });
  };
  processNestedObject(responseData, prefix);
  return initialValues;
};

/**
 * Returns:
 * [editId, PageTitle, currentSubmitFunction, searchParam, isEditPage]
 *
 * PageTitle is:
 *  - string (backward compatible), or
 *  - { text: string; after?: React.ReactNode }  // PageHeader will append `after` after the text
 */
const getEditPageConfig = (formName: string, createFunction?: (values: any) => AxiosRequestConfig, updateFunction?: (values: any) => AxiosRequestConfig): [string | null, string | { text: string; after?: React.ReactNode }, typeof createFunction | undefined, any, boolean] => {
  const urlSearchParams = useMemo(() => queryString.parse(window.location.search), []);
  const editId = useMemo<string | null>(() => {
    const raw = (urlSearchParams as any)?.id;
    if (!raw) return null;
    return Array.isArray(raw) ? String(raw[0]) : String(raw);
  }, [urlSearchParams]);
  const productId = useMemo<string | null>(() => {
    const raw = (urlSearchParams as any)?.productId;
    if (!raw) return null;
    return Array.isArray(raw) ? String(raw[0]) : String(raw);
  }, [urlSearchParams]);
  const searchParam: any = (urlSearchParams as any)?.type ?? productId;
  let isEditPage = false;
  let currentSubmitFunction = createFunction;

  // Default: keep string for backwards compatibility
  let PageTitle: string | { text: string; after?: React.ReactNode } = `Create ${formName}`;
  if (editId != null) {
    isEditPage = true;
    currentSubmitFunction = updateFunction;
    const text = `Edit ${formName}  -  ${editId}`;
    const after = (
      <Box component="span" sx={{ display: 'inline-flex', ml: 1 }}><CustomButton title="Copy edit ID" tooltipText="Copy ID" iconButton size="small" icon={<ContentCopyIcon fontSize="inherit" />} onClick={() => navigator.clipboard?.writeText(editId)} /></Box>
    );

    // Compound title so PageHeader can show text in Helmet and append the copy button visually.
    PageTitle = { text, after };
  }
  return [editId, PageTitle, currentSubmitFunction, searchParam, isEditPage];
};

const mapToOption = <T,>(items: T[], key?: string | number | (string | number)[], customValueKey?: string | string[]): SelectItem[] => {
  const getValue = (obj: any, keyInput: string | number | (string | number)[]): any => {
    if (Array.isArray(keyInput)) {
      return keyInput.reduce((result, keyPart) => (result != null ? result[keyPart] : undefined), obj);
    } else {
      return obj[keyInput];
    }
  };
  let options: SelectItem[] = [];
  if (items != null && items.length > 0) {
    items.forEach((item: any) => {
      if (key == null) {
        options.push({ value: item["Key"], label: item["Value"]?.toString() ?? "", });
      } else if (customValueKey != null) {
        options.push({ value: getValue(item, key), label: (Array.isArray(customValueKey) ? getValue(item, customValueKey) : getValue(item, customValueKey))?.toString() ?? "", });
      } else {
        const nested = getValue(item, key);
        options.push({ value: nested ? nested["Key"] : undefined, label: nested ? nested["Value"]?.toString() ?? "" : "", });
      }
    });
  }
  return options;
};

const FormHelper = { mapToInitialValues, getEditPageConfig, mapToOption, };

export default FormHelper;

export interface SelectItem {
  value: string | number;
  label: string;
}
